import { useAuthStore } from '@/stores/auth'

/**
 * Auth Token Utility Class
 * Provides methods to get valid authentication tokens for API calls
 */
export class AuthTokenUtil {
  /**
   * Private method to decode JWT token and extract payload
   * @returns any | null - Decoded payload or null if invalid
   */
  private static decodeJWTPayload(): any | null {
    try {
      const authStore = useAuthStore()
      
      const idToken = authStore.idToken
      if (!idToken) {
        return null
      }
      
      const tokenParts = idToken.split('.')
      if (tokenParts.length !== 3) {
        return null
      }
      
      const payloadPart = tokenParts[1]
      if (!payloadPart) {
        return null
      }
      
      return JSON.parse(atob(payloadPart))
    } catch (error) {
      console.error('Error decoding JWT token:', error)
      return null
    }
  }

  /**
   * Get a valid access token for API calls
   * Automatically refreshes if token is expired
   * @returns Promise<string | null> - Valid access token or null if not authenticated
   */
  static async getValidToken(): Promise<string | null> {
    try {
      const authStore = useAuthStore()
      return await authStore.getValidAccessToken()
    } catch (error) {
      console.error('Error getting valid access token:', error)
      return null
    }
  }

  /**
   * Get a valid identity token for API calls
   * Automatically refreshes if token is expired
   * @returns Promise<string | null> - Valid identity token or null if not authenticated
   */
  static async getValidIdToken(): Promise<string | null> {
    try {
      const authStore = useAuthStore()
      return await authStore.getValidIdToken()
    } catch (error) {
      console.error('Error getting valid identity token:', error)
      return null
    }
  }

  /**
   * Get authorization headers for API requests
   * @returns Promise<Record<string, string>> - Headers object with Bearer token
   */
  static async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.getValidToken()
    
    if (!token) {
      throw new Error('No valid authentication token available')
    }

    return {
      'Authorization': `Bearer ${token}`
    }
  }

  /**
   * Check if user is currently authenticated
   * @returns boolean - True if authenticated
   */
  static isAuthenticated(): boolean {
    const authStore = useAuthStore()
    return authStore.isLoggedIn
  }

  /**
   * Get the current user's account ID from JWT token
   * @returns string | null - Account ID or null if not found
   */
  static getAccountId(): string | null {
    try {
      const payload = this.decodeJWTPayload()
      return payload?.accountId || null
    } catch (error) {
      console.error('Error extracting account ID from token:', error)
      return null
    }
  }

  /**
   * Check if the current user is a tenant admin
   * @returns boolean - True if user has admin privileges
   */
  static isTenantAdmin(): boolean {
    try {
      const payload = this.decodeJWTPayload()
      const roles = payload?.roles
      return Array.isArray(roles) && roles.includes('TenantAdmin')
    } catch (error) {
      console.error('Error checking admin status:', error)
      return false
    }
  }

  /**
   * Get the current user's tenant ID from JWT token
   * @returns string | null - Tenant ID or null if not found
   */
  static getTenantId(): string | null {
    try {
      const payload = this.decodeJWTPayload()
      return payload?.tenantId || null
    } catch (error) {
      console.error('Error extracting tenant ID from token:', error)
      return null
    }
  }

  /**
   * Get the current user's email address
   * @returns string | null - User email or null if not authenticated
   */
  static getUserEmail(): string | null {
    try {
      const authStore = useAuthStore()
      
      // First try to get from auth store
      if (authStore.userEmail) {
        return authStore.userEmail
      }
      
      // Fallback to localStorage
      const userInfo = localStorage.getItem('user_info')
      if (userInfo) {
        const parsedUserInfo = JSON.parse(userInfo)
        return parsedUserInfo.email || null
      }
      
      return null
    } catch (error) {
      console.error('Error getting user email:', error)
      return null
    }
  }

  /**
   * Handle logout and redirect to home page
   * Clears authentication state and redirects user
   * @param redirectToHome - If true, redirects to home page instead of full logout flow
   */
  static async handleLogout(redirectToHome: boolean = false): Promise<void> {
    try {
      const authStore = useAuthStore()

      if (redirectToHome) {
        // Clear authentication state and redirect to home page
        authStore.clearAuthentication()
        if (typeof window !== 'undefined') {
          window.location.href = '/'
        }
      } else {
        // Full logout flow with Cognito logout
        await authStore.logout()
      }
    } catch (error) {
      console.error('Error during logout:', error)
      // Force clear local state even if logout fails
      const authStore = useAuthStore()
      authStore.clearAuthentication()

      // Force redirect to home
      if (typeof window !== 'undefined') {
        window.location.href = '/'
      }
    }
  }
}